'use client';

import { ChangeEvent } from 'react';
import { ContactData } from './DiagnosticFormInteractive';

interface StepContactProps {
  data: ContactData;
  onChange: (data: ContactData) => void;
}

const POSITION_OPTIONS = [
  'Presidente da Junta de Freguesia',
  'Vice-Presidente',
  'Secretário(a)',
  'Tesoureiro(a)',
  'Vogal',
  'Técnico(a) Administrativo(a)',
  'Outro',
];

const EMPLOYEE_OPTIONS = ['1-5', '6-10', '11-20', '21-50', 'Mais de 50'];
const VOTER_OPTIONS = ['Menos de 1.000', '1.000 - 5.000', '5.001 - 15.000', '15.001 - 50.000', 'Mais de 50.000'];

export default function StepContact({ data, onChange }: StepContactProps) {
  const handleChange = (field: keyof ContactData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const inputClass =
    'w-full h-11 px-4 border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 placeholder:text-gray-400';
  const inputStyle = { fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', '--tw-ring-color': '#009688' } as React.CSSProperties;
  const labelClass = 'block font-semibold text-sm mb-1.5 text-gray-700';
  const labelStyle = { fontFamily: 'DM Sans, sans-serif' };
  const requiredMark = <span className="text-error ml-0.5">*</span>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: '5px', backgroundColor: '#E0F2F1', color: '#009688' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div>
            <h2
              className="text-gray-900 mb-1"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}
            >
              Informações de Contacto e Entidade
            </h2>
            <p
              className="text-gray-500 text-sm leading-relaxed"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Preencha os dados da sua Junta de Freguesia para personalizarmos o diagnóstico e os resultados.
            </p>
          </div>
        </div>
      </div>

      {/* Personal Info */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-5 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <rect x="2" y="7" width="20" height="14" rx="2"/>
            <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
          </svg>
          Dados Pessoais
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="name">
              Nome Completo {requiredMark}
            </label>
            <input
              id="name"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Ex: João Silva"
              value={data.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="position">
              Cargo / Função {requiredMark}
            </label>
            <select
              id="position"
              className={inputClass}
              style={inputStyle}
              value={data.position}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('position', e.target.value)}
            >
              <option value="">Selecione o cargo</option>
              {POSITION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="email">
              Endereço de Email {requiredMark}
            </label>
            <input
              id="email"
              type="email"
              className={inputClass}
              style={inputStyle}
              placeholder="Ex: joao.silva@jf-exemplo.pt"
              value={data.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="phone">
              Telefone / Telemóvel {requiredMark}
            </label>
            <input
              id="phone"
              type="tel"
              className={inputClass}
              style={inputStyle}
              placeholder="Ex: +351 912 345 678"
              value={data.phone}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('phone', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Entity Info */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-5 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          Dados da Entidade
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="parishCouncil">
              Junta de Freguesia {requiredMark}
            </label>
            <input
              id="parishCouncil"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Ex: Junta de Freguesia de Exemplo"
              value={data.parishCouncil}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('parishCouncil', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="municipality">
              Município {requiredMark}
            </label>
            <input
              id="municipality"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Ex: Lisboa"
              value={data.municipality}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('municipality', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="employeeCount">
              Número de Colaboradores {requiredMark}
            </label>
            <select
              id="employeeCount"
              className={inputClass}
              style={inputStyle}
              value={data.employeeCount}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('employeeCount', e.target.value)}
            >
              <option value="">Selecione o intervalo</option>
              {EMPLOYEE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} style={labelStyle} htmlFor="voterCount">
              Número de Eleitores {requiredMark}
            </label>
            <select
              id="voterCount"
              className={inputClass}
              style={inputStyle}
              value={data.voterCount}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('voterCount', e.target.value)}
            >
              <option value="">Selecione o intervalo</option>
              {VOTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Privacy note */}
      <div
        className="flex items-start gap-3 px-5 py-4"
        style={{ borderRadius: '5px', border: '1px solid #E0F2F1', backgroundColor: '#F0FAFA' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" className="mt-0.5 flex-shrink-0" aria-hidden="true">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <polyline points="9 12 11 14 15 10"/>
        </svg>
        <p
          className="text-gray-500 text-xs leading-relaxed"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Os seus dados são tratados de forma confidencial e utilizados exclusivamente para personalizar o diagnóstico e enviar os resultados. Não partilhamos informações com terceiros, em conformidade com o RGPD.
        </p>
      </div>
    </div>
  );
}